import React, { useState } from 'react';
import { Calculator, X, RotateCw, Drill, Grid3x3, Minus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const FloatingCalculator = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('turning');
  
  // States for each calculator
  const [turningInputs, setTurningInputs] = useState({ diameter: '', cuttingSpeed: '', feedPerRev: '' });
  const [turningResults, setTurningResults] = useState(null);
  
  const [millingInputs, setMillingInputs] = useState({ diameter: '', cuttingSpeed: '', teethCount: '', feedPerTooth: '' });
  const [millingResults, setMillingResults] = useState(null);
  
  const [drillingInputs, setDrillingInputs] = useState({ diameter: '', cuttingSpeed: '', feedPerRev: '' });
  const [drillingResults, setDrillingResults] = useState(null);
  
  const [groovingInputs, setGroovingInputs] = useState({ diameter: '', cuttingSpeed: '', feedPerRev: '' });
  const [groovingResults, setGroovingResults] = useState(null);

  const calculateTurning = () => {
    const d = parseFloat(turningInputs.diameter);
    const vc = parseFloat(turningInputs.cuttingSpeed);
    const f = parseFloat(turningInputs.feedPerRev);
    if (!d || !vc) return;
    
    const spindleSpeed = (vc * 1000) / (Math.PI * d);
    const feedRate = f ? f * spindleSpeed : null;
    
    setTurningResults({
      spindleSpeed: spindleSpeed.toFixed(0),
      feedRate: feedRate ? feedRate.toFixed(1) : '-'
    });
  };

  const calculateMilling = () => {
    const d = parseFloat(millingInputs.diameter);
    const vc = parseFloat(millingInputs.cuttingSpeed);
    const z = parseFloat(millingInputs.teethCount);
    const fz = parseFloat(millingInputs.feedPerTooth);
    if (!d || !vc) return;
    
    const spindleSpeed = (vc * 1000) / (Math.PI * d);
    const tableFeed = (z && fz) ? fz * z * spindleSpeed : null;
    
    setMillingResults({
      spindleSpeed: spindleSpeed.toFixed(0),
      tableFeed: tableFeed ? tableFeed.toFixed(1) : '-'
    });
  };

  const calculateDrilling = () => {
    const d = parseFloat(drillingInputs.diameter);
    const vc = parseFloat(drillingInputs.cuttingSpeed);
    const f = parseFloat(drillingInputs.feedPerRev);
    if (!d || !vc) return;
    
    const spindleSpeed = (vc * 1000) / (Math.PI * d);
    const feedRate = f ? f * spindleSpeed : null;
    
    setDrillingResults({
      spindleSpeed: spindleSpeed.toFixed(0),
      feedRate: feedRate ? feedRate.toFixed(1) : '-'
    });
  };

  const calculateGrooving = () => {
    const d = parseFloat(groovingInputs.diameter);
    const vc = parseFloat(groovingInputs.cuttingSpeed);
    const f = parseFloat(groovingInputs.feedPerRev);
    if (!d || !vc) return;
    
    const spindleSpeed = (vc * 1000) / (Math.PI * d);
    const feedRate = f ? f * spindleSpeed : null;
    
    setGroovingResults({
      spindleSpeed: spindleSpeed.toFixed(0),
      feedRate: feedRate ? feedRate.toFixed(1) : '-'
    });
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        className="fixed right-6 bottom-6 z-50 w-14 h-14 bg-[#2E3A6F] text-white rounded-full shadow-2xl hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Calculator size={24} className="group-hover:rotate-12 transition-transform" />
      </motion.button>

      {/* Floating Calculator Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 400 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 400 }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed right-6 bottom-24 z-50 w-96 max-h-[80vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border-2 border-[#2E3A6F]/20"
          >
            <div className="sticky top-0 bg-[#2E3A6F] text-white p-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator size={20} />
                <h3 className="font-semibold">Quick Calculator</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 rounded-full p-1 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-4">
                  <TabsTrigger value="turning" className="text-xs">
                    <RotateCw size={14} />
                  </TabsTrigger>
                  <TabsTrigger value="milling" className="text-xs">
                    <Grid3x3 size={14} />
                  </TabsTrigger>
                  <TabsTrigger value="drilling" className="text-xs">
                    <Drill size={14} />
                  </TabsTrigger>
                  <TabsTrigger value="grooving" className="text-xs">
                    <Minus size={14} />
                  </TabsTrigger>
                </TabsList>

                {/* Turning */}
                <TabsContent value="turning" className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Diameter (mm)</Label>
                    <Input
                      type="number"
                      placeholder="50"
                      value={turningInputs.diameter}
                      onChange={(e) => setTurningInputs({...turningInputs, diameter: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Vc (m/min)</Label>
                    <Input
                      type="number"
                      placeholder="250"
                      value={turningInputs.cuttingSpeed}
                      onChange={(e) => setTurningInputs({...turningInputs, cuttingSpeed: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Feed (mm/rev)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.3"
                      value={turningInputs.feedPerRev}
                      onChange={(e) => setTurningInputs({...turningInputs, feedPerRev: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <Button onClick={calculateTurning} className="w-full h-8 text-sm bg-[#2E3A6F]">
                    Calculate
                  </Button>
                  {turningResults && (
                    <div className="bg-[#2E3A6F]/5 rounded-lg p-3 space-y-2">
                      <div>
                        <p className="text-xs text-gray-600">Spindle Speed</p>
                        <p className="text-lg font-bold text-[#2E3A6F]">{turningResults.spindleSpeed} RPM</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Feed Rate</p>
                        <p className="text-lg font-bold text-[#2E3A6F]">{turningResults.feedRate} mm/min</p>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Milling */}
                <TabsContent value="milling" className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Cutter Ø (mm)</Label>
                    <Input
                      type="number"
                      placeholder="80"
                      value={millingInputs.diameter}
                      onChange={(e) => setMillingInputs({...millingInputs, diameter: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Vc (m/min)</Label>
                    <Input
                      type="number"
                      placeholder="300"
                      value={millingInputs.cuttingSpeed}
                      onChange={(e) => setMillingInputs({...millingInputs, cuttingSpeed: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label className="text-xs">Teeth (z)</Label>
                      <Input
                        type="number"
                        placeholder="8"
                        value={millingInputs.teethCount}
                        onChange={(e) => setMillingInputs({...millingInputs, teethCount: e.target.value})}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">fz (mm)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.15"
                        value={millingInputs.feedPerTooth}
                        onChange={(e) => setMillingInputs({...millingInputs, feedPerTooth: e.target.value})}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <Button onClick={calculateMilling} className="w-full h-8 text-sm bg-[#2E3A6F]">
                    Calculate
                  </Button>
                  {millingResults && (
                    <div className="bg-[#2E3A6F]/5 rounded-lg p-3 space-y-2">
                      <div>
                        <p className="text-xs text-gray-600">Spindle Speed</p>
                        <p className="text-lg font-bold text-[#2E3A6F]">{millingResults.spindleSpeed} RPM</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Table Feed</p>
                        <p className="text-lg font-bold text-[#2E3A6F]">{millingResults.tableFeed} mm/min</p>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Drilling */}
                <TabsContent value="drilling" className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Drill Ø (mm)</Label>
                    <Input
                      type="number"
                      placeholder="10"
                      value={drillingInputs.diameter}
                      onChange={(e) => setDrillingInputs({...drillingInputs, diameter: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Vc (m/min)</Label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={drillingInputs.cuttingSpeed}
                      onChange={(e) => setDrillingInputs({...drillingInputs, cuttingSpeed: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Feed (mm/rev)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.2"
                      value={drillingInputs.feedPerRev}
                      onChange={(e) => setDrillingInputs({...drillingInputs, feedPerRev: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <Button onClick={calculateDrilling} className="w-full h-8 text-sm bg-[#2E3A6F]">
                    Calculate
                  </Button>
                  {drillingResults && (
                    <div className="bg-[#2E3A6F]/5 rounded-lg p-3 space-y-2">
                      <div>
                        <p className="text-xs text-gray-600">Spindle Speed</p>
                        <p className="text-lg font-bold text-[#2E3A6F]">{drillingResults.spindleSpeed} RPM</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Feed Rate</p>
                        <p className="text-lg font-bold text-[#2E3A6F]">{drillingResults.feedRate} mm/min</p>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Grooving */}
                <TabsContent value="grooving" className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Diameter (mm)</Label>
                    <Input
                      type="number"
                      placeholder="40"
                      value={groovingInputs.diameter}
                      onChange={(e) => setGroovingInputs({...groovingInputs, diameter: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Vc (m/min)</Label>
                    <Input
                      type="number"
                      placeholder="150"
                      value={groovingInputs.cuttingSpeed}
                      onChange={(e) => setGroovingInputs({...groovingInputs, cuttingSpeed: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Feed (mm/rev)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.1"
                      value={groovingInputs.feedPerRev}
                      onChange={(e) => setGroovingInputs({...groovingInputs, feedPerRev: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <Button onClick={calculateGrooving} className="w-full h-8 text-sm bg-[#2E3A6F]">
                    Calculate
                  </Button>
                  {groovingResults && (
                    <div className="bg-[#2E3A6F]/5 rounded-lg p-3 space-y-2">
                      <div>
                        <p className="text-xs text-gray-600">Spindle Speed</p>
                        <p className="text-lg font-bold text-[#2E3A6F]">{groovingResults.spindleSpeed} RPM</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Feed Rate</p>
                        <p className="text-lg font-bold text-[#2E3A6F]">{groovingResults.feedRate} mm/min</p>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingCalculator;
